import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { BaseballAverageStatType } from '@sorare/core/src/__generated__/globalTypes';
import { TextSwitch } from '@sorare/core/src/atoms/inputs/TextSwitch';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { Switch } from '@sorare/core/src/components/search/Switch';
import { scoutingCenter } from '@sorare/core/src/lib/glossary';
import { StatsMode } from '@sorare/core/src/lib/playerSearchView';
import { categoryLabels, statLabels } from '@sorare/core/src/lib/scoring';

import {
  hittingBaseballAverageStats,
  pitchingBaseballAverageStats,
} from '#src/lib/scoutingCenter.ts';

const baseballSortableStats = {
  [StatsMode.HITTING]: hittingBaseballAverageStats.map(stat => ({
    value: stat,
    label: (
      <FormattedMessage
        {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
      />
    ),
  })),
  [StatsMode.PITCHING]: pitchingBaseballAverageStats.map(stat => ({
    value: stat,
    label: (
      <FormattedMessage
        {...statLabels[stat.toLowerCase() as keyof typeof statLabels]}
      />
    ),
  })),
  [StatsMode.DEFAULT]: [],
};

const CapitalizeFirstLetter = styled.span`
  &::first-letter {
    text-transform: uppercase;
  }
`;

type Props = {
  stats: BaseballAverageStatType[];
  onChange: (sort: BaseballAverageStatType[]) => void;
  statsMode: StatsMode;
  onStatsModeChange: (statsMode: StatsMode) => void;
};

export const BaseballStats = ({
  stats,
  onChange,
  statsMode,
  onStatsModeChange,
}: Props) => {
  return (
    <Vertical>
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage {...scoutingCenter.statistics} />
      </LabelM>
      <TextSwitch
        value={StatsMode.HITTING}
        compact
        onChange={v => onStatsModeChange(v)}
        options={[
          {
            label: (
              <CapitalizeFirstLetter>
                <FormattedMessage {...categoryLabels.HITTING} />
              </CapitalizeFirstLetter>
            ),
            value: StatsMode.HITTING,
          },
          {
            label: (
              <CapitalizeFirstLetter>
                <FormattedMessage {...categoryLabels.PITCHING} />
              </CapitalizeFirstLetter>
            ),
            value: StatsMode.PITCHING,
          },
        ]}
      />
      {baseballSortableStats[statsMode]?.map(({ value, label }) => (
        <Switch
          key={value}
          label={
            <Horizontal>
              <CapitalizeFirstLetter>{label}</CapitalizeFirstLetter>
            </Horizontal>
          }
          checked={stats.some(v => v === value)}
          onChange={() => {
            onChange(
              stats.includes(value)
                ? stats.filter(v => v !== value)
                : [...stats, value]
            );
          }}
        />
      ))}
    </Vertical>
  );
};
